import { Fragment } from 'react';
import { melbourneDate } from '../../../shared/dates';
import { formatDayMonth } from '../../components/primitives';
import { AutomationHistorySheet, KnobStepper } from '../../components/automationsMobile';
import { AUTOMATION_META, isTextKnob, KNOB_META, sendsNoEmail } from './constants';
import { historyItems, morningBriefStatus, runTimeLabel, runTone, stageLabel, timeOnly } from './helpers';
import type { AutomationItem } from './types';
import { HistoryBlock } from './HistoryBlock';
import { KnobInput } from './KnobInput';
import { MorningBriefFoot } from './MorningBriefFoot';
import { TextKnobInput } from './TextKnobInput';

export function AutomationCard({
  item,
  isMobile,
  emailPaused,
  busy,
  expanded,
  templateCount,
  onToggleExpanded,
  onToggleEnabled,
  onSaveKnob,
  onEditTemplate,
}: {
  item: AutomationItem;
  isMobile: boolean;
  emailPaused: boolean;
  busy: boolean;
  expanded: boolean;
  templateCount: number;
  onToggleExpanded: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onSaveKnob: (knobKey: string, value: number | string) => Promise<void>;
  onEditTemplate: () => void;
}) {
  const meta = AUTOMATION_META[item.key];
  const isLadder = item.key === 'invoice_reminder_ladder';
  const editableKnobKeys = Object.keys(item.knobs).filter(
    (k) => k !== 'stage_offsets_days' && !item.read_only_knobs.includes(k),
  );
  const topError = item.recent_errors[0] ?? null;
  const latestEmail = item.recent_emails[0] ?? null;
  const isBrief = item.key === 'morning_brief';
  const briefStatus = isBrief ? morningBriefStatus(item.last_run_at, item.last_result, item.enabled !== false) : null;
  const routineUrl = typeof item.knobs.routine_url === 'string' ? item.knobs.routine_url.trim() : '';
  const tone = briefStatus?.tone ?? runTone(item.last_result);
  const runPrefix = meta.cadence === 'Event-triggered' ? 'Last fired' : item.last_run_at !== null ? 'Last run' : 'Last sent';
  const sendsHidden = sendsNoEmail(meta.audience);
  const suppressedByPause = emailPaused && !sendsNoEmail(meta.audience);
  // The brief's error log keeps old misses; only surface one while the
  // heartbeat itself is in error, so a healthy card stays clean (M5 left).
  const shownError = isBrief ? (tone === 'err' ? topError : null) : topError;

  return (
    <div className={`atm-card${!item.toggleable || suppressedByPause ? ' muted' : ''}${expanded ? ' expanded' : ''}`}>
      <div className="atm-head">
        <div className="atm-title-block">
          <div className="atm-title">
            {meta.title}
            <span className={`atm-audience${meta.audience === 'you' || meta.audience === 'overview' ? ' you' : ''}${meta.audience === 'none' ? ' none' : ''}`}>
              {meta.audience === 'you' ? '→ You' : meta.audience === 'overview' ? '→ Overview' : meta.audience === 'none' ? '— no email' : 'Client'}
            </span>
          </div>
          <div className="atm-cadence">{meta.cadence}</div>
        </div>
        {item.toggleable ? (
          <button
            type="button"
            className={`${isMobile ? 'toggle' : 'atm-switch'}${item.enabled ? ' on' : ''}`}
            role="switch"
            aria-checked={!!item.enabled}
            aria-label={`Turn ${meta.title} ${item.enabled ? 'off' : 'on'}`}
            disabled={busy}
            onClick={() => onToggleEnabled(!item.enabled)}
          />
        ) : (
          <span className="atm-always">{item.enabled_label ?? 'Always on'}</span>
        )}
        {suppressedByPause && <span className="atm-always">Paused by master switch</span>}
      </div>

      <p className="atm-desc">{item.description}</p>

      {isLadder && (
        <>
          <div className="atm-ladder">
            {((item.knobs.stage_offsets_days as number[] | undefined) ?? []).map((offset, i) => (
              <Fragment key={offset}>
                {i > 0 && <span className="arrow">→</span>}
                <span className="atm-stage-chip">{stageLabel(offset)}</span>
              </Fragment>
            ))}
          </div>
          <div className="atm-ladder-note">
            Fixed sequence — turn the whole ladder off above, but stages can't be reshaped in v1.
          </div>
        </>
      )}

      {/* The number input is too small to hit on a phone — ≤880px gets
          − / + steppers over the same PATCH, clamped to the knob's range
          (mockup §14.1 A). */}
      {!isLadder && editableKnobKeys.length > 0 && (
        <div className={isMobile ? 'atm-m-knobs' : 'atm-knobs'}>
          {editableKnobKeys.map((k) => {
            const known = KNOB_META[k];
            if (isTextKnob(known)) {
              return (
                <TextKnobInput key={k} id={`${item.key}-${k}`} meta={known} isMobile={isMobile} disabled={busy}
                  value={typeof item.knobs[k] === 'string' ? (item.knobs[k] as string) : ''}
                  onSave={(v) => onSaveKnob(k, v)} />
              );
            }
            const meta = known;
            if (k === 'weekday') {
              const select = (
                <select id={`${item.key}-weekday`} className="f-input" disabled={busy}
                  style={isMobile ? { width: 'auto', minWidth: 150 } : undefined}
                  value={item.knobs[k] as number}
                  onChange={(e) => { void onSaveKnob(k, Number(e.target.value)).catch(() => {}); }}>
                  {[[1, 'Monday'], [2, 'Tuesday'], [3, 'Wednesday'], [4, 'Thursday'],
                    [5, 'Friday'], [6, 'Saturday'], [0, 'Sunday']].map(([day, label]) => (
                    <option key={day} value={day}>{label}</option>
                  ))}
                </select>
              );
              // Phone: one row like the steppers (label left, control right).
              if (isMobile) {
                return (
                  <div className="atm-m-knob" key={k}>
                    <label className="k" htmlFor={`${item.key}-weekday`}>
                      {meta.label}
                      <small>Sent on or after this day</small>
                    </label>
                    <span className="stp">{select}</span>
                  </div>
                );
              }
              return (
                <div className="atm-knob" key={k}>
                  <label className="kl" htmlFor={`${item.key}-weekday`}>{meta.label}</label>
                  <div className="kv">{select}</div>
                </div>
              );
            }
            if (isMobile && meta) {
              return (
                <KnobStepper
                  key={k}
                  label={meta.label}
                  unit={meta.unit}
                  range={{ min: meta.min, max: meta.max }}
                  value={item.knobs[k] as number}
                  busy={busy}
                  onSave={(v) => onSaveKnob(k, v)}
                />
              );
            }
            return <KnobInput key={k} knobKey={k} value={item.knobs[k] as number} disabled={busy} onSave={(v) => onSaveKnob(k, v)} />;
          })}
        </div>
      )}

      <div className={`atm-status${tone === 'idle' ? ' idle' : tone === 'err' ? ' err' : tone === 'paused' ? ' paused' : ''}`}>
        <span className="dot" />
        {briefStatus ? (
          briefStatus.text === null ? 'Not run yet' : (
            <>
              {briefStatus.prefix} · {tone === 'err' ? <span className="err-text">{briefStatus.text}</span> : briefStatus.text}
              {briefStatus.missed && (routineUrl
                ? <> · <a className="atm-status-link" href={routineUrl} target="_blank" rel="noreferrer">check the routine ↗</a></>
                : ' · check the routine')}
            </>
          )
        ) : item.last_run_at !== null ? (
          <>
            {runPrefix} {runTimeLabel(item.last_run_at)}
            {item.last_result && (
              <>
                {' · '}
                {tone === 'err' ? <span className="err-text">error — {item.last_result}</span> : item.last_result}
              </>
            )}
          </>
        ) : latestEmail ? (
          <>
            {runPrefix} {formatDayMonth(melbourneDate(latestEmail.created_at))} ·{' '}
            {latestEmail.subject ?? latestEmail.to_email ?? 'sent'}
          </>
        ) : (
          'Not run yet'
        )}
      </div>

      {shownError && (isBrief ? (
        <div className="atm-error-row">
          <span className="t">{shownError.scope}</span>
          <span className="m">
            {/^No brief received/.test(shownError.message)
              ? 'The routine did not post a brief by midday. Check its last run on claude.ai, then press Regenerate on next run and Run now.'
              : shownError.message}
          </span>
        </div>
      ) : (
        <div className="atm-error-row">
          <span className="t">{timeOnly(shownError.created_at)}</span>
          <span className="m">
            {shownError.scope} — {shownError.message}
          </span>
        </div>
      ))}

      {isBrief && (
        <MorningBriefFoot routineUrl={routineUrl} isMobile={isMobile} />
      )}

      {!sendsHidden && !isMobile && (
        <div className="atm-foot">
          {templateCount > 0 && (
            <span className="atm-foot-link" onClick={onEditTemplate}>
              {templateCount > 1 ? `Templates · ${templateCount} stages → Edit` : 'Template → Edit'}
            </span>
          )}
          <span className="atm-foot-link primary" onClick={onToggleExpanded}>
            Recent sends ({item.recent_emails.length}){!expanded && ' →'}
          </span>
        </div>
      )}

      {/* Both drill-downs are buttons that open sheets on mobile; the send
          history no longer expands the card in place (mockup §14.1 A/B). */}
      {!sendsHidden && isMobile && (
        <div className="atm-m-btns">
          {templateCount > 0 && (
            <button type="button" className="cbtn" onClick={onEditTemplate}>
              {templateCount > 1 ? `Templates · ${templateCount} stages` : 'Template'}
            </button>
          )}
          <button type="button" className="cbtn" onClick={onToggleExpanded}>
            Recent sends ({item.recent_emails.length})
          </button>
        </div>
      )}

      {expanded && !sendsHidden && !isMobile && <HistoryBlock rows={item.recent_emails} />}
      {!sendsHidden && isMobile && (
        <AutomationHistorySheet
          open={expanded}
          onClose={onToggleExpanded}
          title={meta.title}
          items={historyItems(item.recent_emails)}
        />
      )}
    </div>
  );
}
